import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';
import { useSubscription } from '@/context/SubscriptionContext';
import { Bell, Crown, Save, Check, Settings, Smartphone, MessageSquare } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, Link } from 'react-router-dom';
import { NotificationToggle } from '@/components/PushNotificationManager';
import SEO from '@/components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PARTIES = [
  { id: 'labour', name: 'Labour', color: 'bg-red-500' },
  { id: 'conservative', name: 'Conservative', color: 'bg-blue-500' },
  { id: 'libdem', name: 'Lib Dems', color: 'bg-orange-500' },
  { id: 'snp', name: 'SNP', color: 'bg-yellow-500' },
  { id: 'reform', name: 'Reform UK', color: 'bg-cyan-500' },
  { id: 'green', name: 'Green', color: 'bg-green-500' },
  { id: 'plaid', name: 'Plaid Cymru', color: 'bg-emerald-600' },
  { id: 'dup', name: 'DUP', color: 'bg-red-800' },
  { id: 'sinnfein', name: 'Sinn Féin', color: 'bg-green-700' },
];

const REGIONS = [
  "London", "Greater Manchester", "West Midlands", "West Yorkshire", "South Yorkshire",
  "Merseyside", "Bristol", "Liverpool", "Birmingham", "Leeds", "Sheffield", "Newcastle",
  "Belfast", "Cardiff", "Edinburgh", "Glasgow", "Aberdeen", "Dundee", "Swansea",
  "Cambridge", "Oxford", "Brighton", "Reading", "Milton Keynes", "York"
];

export const SettingsPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { isPremium } = useSubscription();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState({
    alert_parties: [],
    alert_regions: [],
    alert_frequency: 'instant',
    email_alerts_enabled: true,
    sms_alerts_enabled: false,
    phone_number: ''
  });

  useEffect(() => {
    if (!user) {
      navigate('/login?redirect=/settings');
      return;
    }
    fetchPreferences();
  }, [user, navigate]);

  const fetchPreferences = async () => {
    try {
      const response = await axios.get(`${API}/user/preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreferences(response.data);
    } catch (error) {
      console.error('Failed to fetch preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/user/preferences`, preferences, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Preferences saved successfully!');
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const toggleParty = (partyId) => {
    setPreferences(prev => ({
      ...prev,
      alert_parties: prev.alert_parties.includes(partyId)
        ? prev.alert_parties.filter(p => p !== partyId)
        : [...prev.alert_parties, partyId]
    }));
  };

  const toggleRegion = (region) => {
    setPreferences(prev => ({
      ...prev,
      alert_regions: prev.alert_regions.includes(region)
        ? prev.alert_regions.filter(r => r !== region)
        : [...prev.alert_regions, region]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white pb-20" data-testid="settings-page">
      <SEO title="Settings" description="Manage your notification preferences and account settings" />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-12">
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-white/10 rounded-xl">
              <Settings className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-black">Settings</h1>
              <p className="text-slate-300">Manage your notification preferences</p>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Premium Status */}
        {!isPremium && (
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-6 mb-8 shadow-sm">
            <div className="flex items-center gap-4">
              <div className="bg-amber-100 p-3 rounded-full">
                <Crown className="h-6 w-6 text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-lg">Upgrade to Premium</h3>
                <p className="text-sm text-slate-600">Email alerts are a Premium feature. Upgrade to get instant notifications.</p>
              </div>
              <Button onClick={() => navigate('/subscribe')} className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 font-bold shadow-lg">
                Upgrade
              </Button>
            </div>
          </div>
        )}

      {/* Push Notifications - FREE for all users */}
      <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6 shadow-sm">
        <h3 className="font-bold text-lg mb-3 flex items-center gap-2">
          <Bell className="h-5 w-5 text-purple-600" />
          Push Notifications (Free)
        </h3>
        <NotificationToggle />
        <p className="text-sm text-slate-500 mt-3">Get instant browser/mobile alerts for breaking political news</p>
      </div>

      {/* Email Alerts Toggle */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Bell className="h-5 w-5 text-slate-600" />
            <div>
              <h3 className="font-semibold">Email Alerts</h3>
              <p className="text-sm text-slate-500">Receive notifications for articles matching your interests</p>
            </div>
          </div>
          <button
            onClick={() => setPreferences(prev => ({ ...prev, email_alerts_enabled: !prev.email_alerts_enabled }))}
            className={`relative w-12 h-6 rounded-full transition-colors ${
              preferences.email_alerts_enabled ? 'bg-green-500' : 'bg-slate-300'
            }`}
            disabled={!isPremium}
            data-testid="email-alerts-toggle"
          >
            <div className={`absolute w-5 h-5 bg-white rounded-full top-0.5 transition-transform ${
              preferences.email_alerts_enabled ? 'translate-x-6' : 'translate-x-0.5'
            }`} />
          </button>
        </div>
      </div>

      {/* SMS Alerts Section */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-6 shadow-sm">
        <div className="flex items-center gap-3 mb-4">
          <Smartphone className="h-5 w-5 text-emerald-600" />
          <div>
            <h3 className="font-bold text-lg">SMS Alerts</h3>
            <p className="text-sm text-slate-500">Get text messages when watched politicians appear in the news</p>
          </div>
        </div>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Enable SMS Alerts</span>
            <button
              onClick={() => setPreferences(prev => ({ ...prev, sms_alerts_enabled: !prev.sms_alerts_enabled }))}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                preferences.sms_alerts_enabled ? 'bg-emerald-500' : 'bg-slate-300'
              }`}
              disabled={!isPremium}
              data-testid="sms-alerts-toggle"
            >
              <div className={`absolute w-5 h-5 bg-white rounded-full top-0.5 transition-transform ${
                preferences.sms_alerts_enabled ? 'translate-x-6' : 'translate-x-0.5'
              }`} />
            </button>
          </div>
          
          {preferences.sms_alerts_enabled && (
            <div>
              <label className="text-sm font-medium mb-2 block">Phone Number (UK)</label>
              <div className="flex gap-2">
                <span className="flex items-center px-3 bg-slate-100 rounded-lg text-slate-500 text-sm">+44</span>
                <Input
                  type="tel"
                  placeholder="7700 900123"
                  value={preferences.phone_number?.replace(/^\+44/, '').replace(/^0/, '') || ''}
                  onChange={(e) => setPreferences(prev => ({ 
                    ...prev, 
                    phone_number: e.target.value.replace(/[^0-9]/g, '')
                  }))}
                  className="flex-1"
                  maxLength={11}
                  disabled={!isPremium}
                  data-testid="phone-number-input"
                />
              </div>
              <p className="text-xs text-slate-400 mt-2">Standard message rates may apply. UK numbers only.</p>
            </div>
          )}
          
          <Link 
            to="/watchlist" 
            className="flex items-center gap-2 text-sm text-emerald-600 hover:text-emerald-700 font-medium"
          >
            <MessageSquare className="h-4 w-4" />
            Manage your politician watchlist
          </Link>
        </div>
      </div>

      {/* Party Interests */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-6 shadow-sm">
        <h3 className="font-bold text-lg mb-2">Parties of Interest</h3>
        <p className="text-sm text-slate-500 mb-4">Select parties to receive alerts about</p>
        <div className="flex flex-wrap gap-2">
          {PARTIES.map(party => (
            <button
              key={party.id}
              onClick={() => toggleParty(party.id)}
              disabled={!isPremium}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                preferences.alert_parties.includes(party.id)
                  ? `${party.color} text-white`
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              } ${!isPremium ? 'opacity-50 cursor-not-allowed' : ''}`}
              data-testid={`party-${party.id}`}
            >
              {preferences.alert_parties.includes(party.id) && <Check className="h-4 w-4" />}
              {party.name}
            </button>
          ))}
        </div>
      </div>

      {/* Region Interests */}
      <div className="bg-white border border-slate-200 rounded-2xl p-6 mb-6 shadow-sm">
        <h3 className="font-bold text-lg mb-2">Regions of Interest</h3>
        <p className="text-sm text-slate-500 mb-4">Select regions to receive alerts about</p>
        <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto">
          {REGIONS.map(region => (
            <button
              key={region}
              onClick={() => toggleRegion(region)}
              disabled={!isPremium}
              className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                preferences.alert_regions.includes(region)
                  ? 'bg-slate-900 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              } ${!isPremium ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {region}
            </button>
          ))}
        </div>
      </div>

      {/* Save Button */}
      <Button 
        onClick={handleSave} 
        disabled={saving || !isPremium}
        className="w-full py-6 bg-gradient-to-r from-slate-900 to-slate-800 hover:from-slate-800 hover:to-slate-700 font-bold text-lg shadow-lg"
        data-testid="save-preferences-btn"
      >
        {saving ? 'Saving...' : (
          <>
            <Save className="h-5 w-5 mr-2" /> Save Preferences
          </>
        )}
      </Button>
      </div>
    </div>
  );
};

export default SettingsPage;
